import { throttle } from "lodash-es";
import { storeToRefs } from "pinia";
import { computed, ref, watch } from "vue";

import { Artefact } from "@/lib/artefacts";
import { getEvidenceInstructionsArtefact } from "@/lib/artefacts/evidence_instructions";
import { getDescriptionArtefact } from "@/lib/artefacts/markdown";
import { api, type types } from "@/lib/openapi";
import { followLink } from "@/lib/openapi/helpers";
import { withThreadRevision } from "@/lib/thread-revision-link";
import router from "@/router";
import { EntityType, useCurrentEntityManager } from "@/stores/entity-store";
import { defineEntityStore } from "@/stores/helpers";
import { useReleaseStore } from "@/stores/release-store";
import { useSyncStore } from "@/stores/sync-store";

import {
    FILE_SYNC_THROTTLE_MS,
    discardDraftsAfterSync,
    useDraftManager,
} from "../composables/draft-manager";
import { useEditManager } from "../composables/edit-manager";
import { registerFlush } from "../composables/pending-flush";
import {
    refreshOnCrossTabEntitySync,
    wouldClobberLocalChanges,
} from "../composables/refresh-on-entity-change";
import { clearStaleContentOnSwitch } from "../composables/stale-while-revalidate";
import {
    isAbortError,
    useLatestRequest,
} from "../composables/use-latest-request";
import { useProcessElementsStore } from "./process-elements-store";

export type Task = types.ProcessTask;

// Uses the current route to determine what the 'task' should be and fetches
// the task description for you.
export const useTaskStore = defineEntityStore("task", () => {
    const task = ref<Task>();
    const syncStore = useSyncStore();
    const entityId = computed(() => task.value?.identity);
    // Local draft of the "generates evidence" toggle. Kept in sync with the
    // loaded task and folded into the same throttled save as
    // evidenceInstructions below, so both fields always travel together to
    // the update_task_evidence endpoint — see queueSave. Two independent
    // saves used to race here (this toggle from TaskPage.vue, the text from
    // here), each sending a partial payload that could clobber the other.
    const generatesEvidence = ref(false);
    watch(
        () => task.value,
        (newTask) => {
            if (newTask) {
                generatesEvidence.value = newTask.generates_evidence ?? false;
            }
        },
        { immediate: true }
    );
    const generatesEvidenceModified = computed(
        () =>
            task.value !== undefined &&
            generatesEvidence.value !== (task.value.generates_evidence ?? false)
    );
    const uiCode = ref<Artefact<"text/javascript">>();
    const description = ref<Artefact<"text/markdown">>();
    const evidenceInstructions = ref<Artefact<"text/markdown">>();
    const descriptionDraft = useDraftManager({
        entityId,
        entityType: EntityType.TASK,
        attribute: "description",
        artefact: description,
    });
    const evidenceInstructionsDraft = useDraftManager({
        entityId,
        entityType: EntityType.TASK,
        attribute: "evidence_instructions",
        artefact: evidenceInstructions,
    });

    const { refresh: refreshProcessElements } = useProcessElementsStore();

    discardDraftsAfterSync(
        entityId,
        [descriptionDraft, evidenceInstructionsDraft],
        EntityType.TASK
    );

    const {
        organization,
        stek,
        process,
        task: taskInfo,
    } = useCurrentEntityManager();
    const releaseStore = useReleaseStore();
    const revisionQuery = computed(() =>
        releaseStore.activeRevision
            ? new URLSearchParams({ revision: releaseStore.activeRevision })
            : undefined
    );
    const isFetching = ref(false);
    const isFetchingDescription = ref(false);
    const isFetchingUiCode = ref(false);
    const isFetchingEvidenceInstructions = ref(false);
    // Latest-request guard: each updateTask gets its own AbortController + token,
    // so a stale run can neither abort the current one nor commit results.
    const { begin, isCurrent } = useLatestRequest();

    async function updateTask(force = false) {
        const taskId = taskInfo.value?.identity;

        // In case the same task is selected, return early.
        if (!force && task.value && task.value.identity === taskId) {
            return;
        }

        // Start a new request: aborts the previous one and gives us our own
        // signal + token (read locally, never a shared ref) — see useLatestRequest.
        const { token, signal } = begin();

        if (!taskId) {
            description.value = undefined;
            task.value = undefined;
            return;
        }

        isFetching.value = true;
        // Stale-while-revalidate: keep the previously-loaded task + artefacts on
        // screen while the *same* task is refetched (a revision switch or a
        // background sync), so it doesn't blank the page or flash the spinner.
        // Switching to a *different* task is handled by clearStaleContentOnSwitch,
        // which blanks these refs so the old task never shows on the new page.

        try {
            const { response, data } =
                await api.startProcessProcessElementMgtGetTaskStartPost({
                    body: {
                        task_id: `${taskId}`,
                        include_archived: true,
                    },
                    signal,
                });

            // A newer request superseded us while fetching — commit nothing.
            if (!isCurrent(token)) {
                return;
            }

            if (!response.ok) {
                if (response.status === 404) {
                    if (!releaseStore.activeRevision) {
                        router.replace({
                            name: "not-found",
                            params: {
                                orgName: organization.value?.name,
                                stekName: stek.value?.name,
                            },
                            query: {
                                type: "task",
                                id: taskId,
                            },
                        });
                    }
                    return;
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            // Landing-time clobber guard: edits may have appeared while this
            // GET was in flight — the notification-time guard can't see
            // them. Only a same-task refetch is dropped; navigating to a
            // different task must always commit. A dropped commit
            // self-heals: the edit's own sync re-notifies and refetches.
            if (
                taskId === task.value?.identity &&
                wouldClobberLocalChanges(externalRefresh)
            ) {
                return;
            }
            task.value = data;

            // Sub-fetches load on the SAME signal/token so an abort or a newer
            // request cleanly supersedes them too, and isFetching stays true
            // until the artefacts are ready, not just the task row.
            if (task.value) {
                await Promise.all([
                    updateDescription(signal, token),
                    updateUiCode(signal, token),
                    updateEvidenceInstructions(signal, token),
                ]);
            }
        } catch (e) {
            if (isAbortError(e)) {
                return;
            }
            throw e;
        } finally {
            // Only the current request owns the page-level fetch flag.
            if (isCurrent(token)) {
                isFetching.value = false;
            }
        }
    }

    // Drop the previous task's content when the route switches to a different
    // task, so stale-while-revalidate never renders the old task on the new page.
    // Includes uiCode + evidenceInstructions: their panels render as soon as the
    // new task row arrives, so a stale artefact would briefly show on it.
    clearStaleContentOnSwitch(
        () => taskInfo.value?.identity,
        [task, description, uiCode, evidenceInstructions]
    );

    // Watch for task identity changes
    watch(
        () => taskInfo.value?.identity,
        () => updateTask(),
        { immediate: true }
    );
    watch(
        () => releaseStore.activeRevision,
        () => {
            if (task.value) updateTask(true);
        }
    );

    // Listen for sync events that might affect the task
    syncStore.$onAction(({ name, after }) => {
        if (name === "sync") {
            after((fullySyncedIdentities) => {
                if (
                    fullySyncedIdentities.some(
                        (identity) =>
                            identity.attribute === "process_definition"
                    ) &&
                    task.value &&
                    stek.value
                ) {
                    updateTask(true);
                }
            });
        }
    });

    const updateDescription = async (signal: AbortSignal, token: number) => {
        if (!task.value) {
            return;
        }

        // Always refetch for the (possibly new) current task — under
        // stale-while-revalidate the previous artefact is still set, so a guard
        // on its truthiness would skip and leave stale content.
        isFetchingDescription.value = true;
        try {
            const descriptionResult = await getDescriptionArtefact(
                task.value,
                "taskDescription",
                signal,
                revisionQuery.value
            );

            // A newer request superseded us — commit nothing. Same for
            // edits that appeared while fetching (landing-time guard).
            if (
                !isCurrent(token) ||
                wouldClobberLocalChanges(externalRefresh)
            ) {
                return;
            }

            description.value = descriptionResult;
        } catch (e) {
            if (isAbortError(e)) {
                return;
            }
            throw e;
        } finally {
            if (isCurrent(token)) {
                isFetchingDescription.value = false;
            }
        }
    };

    const updateUiCode = async (signal: AbortSignal, token: number) => {
        if (!task.value) {
            return;
        }

        // Always refetch for the current task (SWR overwrites the stale value).
        if (task.value.ui_code) {
            try {
                isFetchingUiCode.value = true;
                const { response: uiResponse, data } = await followLink<File>(
                    task.value.ui_code,
                    "get",
                    undefined,
                    signal,
                    revisionQuery.value
                );

                // A newer request superseded us — commit nothing.
                if (!isCurrent(token) || !task.value) {
                    return;
                }

                if (!uiResponse.ok) {
                    return;
                }

                const blob =
                    data instanceof Blob ? data : new Blob([data || ""]);

                // Creating an Artefact involves an async blob.text() — re-check.
                const content = await blob.text();

                if (
                    !isCurrent(token) ||
                    !task.value ||
                    wouldClobberLocalChanges(externalRefresh)
                ) {
                    return;
                }

                uiCode.value = new Artefact({
                    name: "taskUiCode",
                    identity: task.value.ui_code.identity,
                    blob: blob,
                    mimetype: blob.type as "text/javascript",
                    content: content,
                });
            } catch (e) {
                if (isAbortError(e)) {
                    return;
                }
                throw e;
            } finally {
                if (isCurrent(token)) {
                    isFetchingUiCode.value = false;
                }
            }
        } else {
            uiCode.value = Artefact.createEmptyArtefact({
                mimetype: "text/javascript",
                identity: task.value.identity + "_ui_code",
            });
        }
    };

    const updateEvidenceInstructions = async (
        signal: AbortSignal,
        token: number
    ) => {
        if (!task.value) {
            return;
        }

        // Always refetch for the current task (SWR overwrites the stale value).
        isFetchingEvidenceInstructions.value = true;
        try {
            const result = await getEvidenceInstructionsArtefact(
                task.value,
                "evidenceInstructions",
                signal,
                revisionQuery.value
            );

            // A newer request superseded us — commit nothing. Same for
            // edits that appeared while fetching (landing-time guard).
            if (
                !isCurrent(token) ||
                wouldClobberLocalChanges(externalRefresh)
            ) {
                return;
            }

            evidenceInstructions.value = result;
        } catch (e) {
            if (isAbortError(e)) {
                return;
            }
            throw e;
        } finally {
            if (isCurrent(token)) {
                isFetchingEvidenceInstructions.value = false;
            }
        }
    };

    // No isFetching short-circuit here: edits made WHILE a refetch is in
    // flight must read as modified, or the landing-time clobber guard can't
    // see them and the refetch's response overwrites the user's text.
    const isModified = computed(() => {
        return Boolean(
            description.value?.isModified ||
            uiCode.value?.isModified ||
            evidenceInstructions.value?.isModified ||
            generatesEvidenceModified.value
        );
    });

    const queueSave = async () => {
        if (!isModified.value || !task.value) {
            return;
        }

        const updateContent: types.StartProcessProcessElementMgtUpdateTaskStartPostData =
            {
                body: {
                    task_id: task.value.identity,
                },
            };

        if (description.value?.isModified) {
            updateContent.body.description = description.value.blob;
        }

        if (uiCode.value?.isModified) {
            updateContent.body.ui_code = uiCode.value.blob;
        }

        const descOrUiCodeModified =
            description.value?.isModified || uiCode.value?.isModified;

        if (descOrUiCodeModified) {
            syncStore.queueSync({
                entityType: EntityType.TASK,
                args: [updateContent],
            });

            if (description.value?.isModified) {
                description.value.persist();
            }

            if (uiCode.value?.isModified) {
                uiCode.value.persist();
            }
        }

        // Evidence (the "generates evidence" toggle and its instructions text)
        // use a separate endpoint. Both fields are always sent together here —
        // whichever changed — so a single request never omits a field the
        // other one still holds, which previously let two independent saves
        // race and clobber each other.
        if (
            (evidenceInstructions.value?.isModified ||
                generatesEvidenceModified.value) &&
            task.value
        ) {
            await api.startProcessProcessElementMgtUpdateTaskEvidenceStartPost({
                body: {
                    task_id: task.value.identity,
                    generates_evidence: generatesEvidence.value,
                    evidence_instructions: evidenceInstructions.value?.blob,
                },
            });

            task.value = {
                ...task.value,
                generates_evidence: generatesEvidence.value,
            };

            if (evidenceInstructions.value?.isModified) {
                evidenceInstructions.value.persist();
            }

            await refreshProcessElements();
        }
    };

    const reset = async () => {
        await description.value?.resetContent();
        await uiCode.value?.resetContent();
        await evidenceInstructions.value?.resetContent();
    };

    const { isEditing } = useEditManager();
    const throttledSave = throttle(queueSave, FILE_SYNC_THROTTLE_MS);
    registerFlush(() => throttledSave.flush());
    watch([isEditing, isModified], ([_, modified]) => {
        if (modified) {
            throttledSave();
        }
    });

    // One shared opts object: the notification guard and the landing-time
    // commit gates inside the update functions must agree on what "would
    // clobber local changes".
    const externalRefresh = {
        entityType: EntityType.TASK,
        currentEntityId: () => task.value?.identity,
        isDirty: () => isModified.value,
        refetch: () => updateTask(true),
    };

    refreshOnCrossTabEntitySync({
        ...externalRefresh,
        enabled: () => !!stek.value,
    });

    const deleteTask = async () => {
        if (!task.value) {
            return;
        }
        if (!task.value.is_archived) {
            throw Error("Can only delete archived tasks");
        }

        await api.startProcessProcessElementMgtDeleteTaskStartPost({
            body: {
                task_id: task.value.identity,
            },
        });

        await refreshProcessElements();

        if (organization.value && stek.value && process.value) {
            await router.push(
                withThreadRevision({
                    name: "processPage",
                    params: {
                        orgName: organization.value.name,
                        stekName: stek.value.name,
                        processId: process.value.identity,
                    },
                })
            );
        }
    };

    return {
        task,
        description,
        uiCode,
        evidenceInstructions,
        generatesEvidence,
        isModified,
        isFetching: computed(() => {
            return (
                isFetching.value ||
                isFetchingDescription.value ||
                isFetchingUiCode.value ||
                isFetchingEvidenceInstructions.value
            );
        }),
        reset,
        deleteTask,
    };
});

export const useTaskManager = () => storeToRefs(useTaskStore());
